// Pure MCP server-name / label helpers — zero dependencies.
//
// Split out of mcpServers.js (which pulls in the analytics-query stack, and
// transitively shell.js's module-load `window` access) so the on-the-fly
// extractors in mcpServerDefs.js — and their unit tests — can use the name
// helpers without dragging the whole query layer (or a DOM) into a node test
// env. mcpServers.js re-exports everything here, so existing importers are
// unaffected.
//
// Claude's MCP namespacing convention is `mcp__<server>__<tool>`. Server "id"
// is the raw underscored segment (e.g. `claude_ai_Linear`) — stable and
// indexable; display labels strip the `mcp__` noise via a small lookup table
// with an underscore-to-space fallback for unknown servers.

// Extract `<server>` from `mcp__<server>__<rest>`. Returns null when the tool
// name isn't an MCP call (any non-`mcp__` prefix).
export function extractServerFromToolName(toolName) {
    if (typeof toolName !== "string") return null;
    if (!toolName.startsWith("mcp__")) return null;
    const parts = toolName.split("__");
    if (parts.length < 3) return null;
    return parts[1] || null;
}

// Lookup of well-known servers → human display. Anything missing falls through
// to underscore-to-space cleanup; servers with mixed casing (`claude_ai_Linear`)
// preserve it because we don't title-case.
const PRETTY_LABELS = {
    claude_ai_Linear:          "claude.ai · Linear",
    claude_ai_Gmail:           "claude.ai · Gmail",
    claude_ai_Google_Calendar: "claude.ai · Google Calendar",
    claude_ai_Google_Drive:    "claude.ai · Google Drive",
    claude_ai_Slack:           "claude.ai · Slack",
    "claude_ai_origin-staging": "claude.ai · origin-staging",
    "plugin_chrome-devtools-mcp_chrome-devtools": "Chrome DevTools",
};

export function labelForMcpServer(id) {
    if (!id) return "";
    if (PRETTY_LABELS[id]) return PRETTY_LABELS[id];
    let label = String(id);
    try { label = decodeURIComponent(label); } catch { /* Older server ids may contain literal percent signs. */ }
    return label.replace(/_/g, " ");
}

// `mcp__<server>__<tool>` → `<tool>` (the bare tool name, server prefix
// stripped). Anything that isn't a 3+ segment MCP name is returned unchanged.
export function shortToolName(toolName) {
    const s = String(toolName || "");
    const parts = s.split("__");
    return parts.length >= 3 ? parts.slice(2).join("__") : s;
}

// Heuristic provenance from the server-id prefix — where the server most
// likely came from. claude.ai connectors and Claude Code plugins use stable
// id prefixes; everything else is unclassified. Display-only (a hint for the
// detail-page hero), never a security assertion.
export function mcpServerProvenance(id) {
    const s = String(id || "");
    if (s.startsWith("claude_ai_")) return "claude.ai connector";
    if (s.startsWith("plugin_")) return "Claude Code plugin";
    return "Custom / self-hosted";
}
